/**
 * Central money formatting — single source of truth for every amount
 * displayed in the app.
 *
 * Canonical API for NEW code:
 *   formatMoney(value, currencyCode, options)
 *
 * Legacy adapters (`toCurrency`, `toCurrencyExplicit`) reproduce the
 * historical $filtersToCurrency output byte-for-byte so the ~65 existing
 * template call sites keep working unchanged.
 */

/**
 * Currency lookup is environment-specific (SecureLS cache / location
 * store) and would create circular imports here — app.js injects it.
 * Shape expected back: { currencyCode, currencySymbol, ... } | null.
 */
let currencyResolver = null;

export function setCurrencyResolver(fn) {
  currencyResolver = fn;
}

/** Returns the currency record for a code, or null when unknown. */
export function getCurrency(currencyCode) {
  if (!currencyCode || typeof currencyResolver !== 'function') return null;
  try {
    return currencyResolver(currencyCode) || null;
  } catch {
    return null;
  }
}

function resolveSymbol(currencyCode) {
  const currency = getCurrency(currencyCode);
  return currency?.currencySymbol ?? '';
}

/**
 * Format an amount for display.
 *
 * @param {*} value            number | numeric string | anything parseable
 * @param {string} currencyCode ISO-ish code shown after the amount (XOF…)
 * @param {object} [options]
 * @param {number}  [options.decimals=2]     fraction digits (0 for XOF-style prices)
 * @param {string}  [options.locale]         BCP-47 tag; defaults to navigator.language
 * @param {boolean} [options.withSymbol=true] prepend resolved currency symbol
 * @param {boolean} [options.withCode=true]  append the raw currency code
 */
export function formatMoney(value, currencyCode = '', options = {}) {
  const {
    decimals = 2,
    locale = typeof navigator === 'undefined' ? 'fr-FR' : navigator.language,
    withSymbol = true,
    withCode = true,
  } = options;

  // Tolerate BigDecimal-as-string, null, undefined, '', garbage.
  const parsed = Number.parseFloat(value);
  const safe = Number.isFinite(parsed) ? parsed : 0;

  let formatted;
  try {
    formatted = safe.toLocaleString(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  } catch {
    formatted = safe.toFixed(decimals);
  }

  const symbol = withSymbol ? resolveSymbol(currencyCode) : '';
  const parts = [symbol, formatted];
  if (withCode && currencyCode) parts.push(` ${currencyCode}`);
  return parts.join('');
}

/**
 * LEGACY adapter — mirrors the original $filtersToCurrency.toCurrency
 * (second argument was always ignored by callers).
 */
export function toCurrency(value, _ignoredCurrencySymbol, currencyCode) {
  return formatMoney(value, currencyCode, { decimals: 2 });
}

/**
 * LEGACY adapter — mirrors $filtersToCurrency0.toCurrency: renders ''
 * unless the caller explicitly passes a symbol.
 */
export function toCurrencyExplicit(value, currencySymbol, currencyCode) {
  if (currencySymbol === undefined) return '';
  const parsed = Number.parseFloat(value) || 0;
  let formatted;
  try {
    formatted = parsed.toLocaleString(
      typeof navigator !== 'undefined' ? navigator.language : 'fr-FR',
      { minimumFractionDigits: 2, maximumFractionDigits: 2 }
    );
  } catch {
    formatted = parsed.toFixed(2);
  }
  return `${currencySymbol}${formatted} ${currencyCode || ''}`.trimEnd();
}

export default { formatMoney, toCurrency, toCurrencyExplicit, getCurrency, setCurrencyResolver };
